import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Megaphone, Check, X, Pause, Play, BarChart2, Plus, Pencil } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

type Ad = {
  id: number; advertiserUserId: number; businessName: string | null;
  headline: string; subtext: string | null; imageUrl: string | null;
  ctaText: string; ctaType: string; ctaValue: string;
  placements: string[]; towns: string[];
  startsAt: string | null; endsAt: string | null; durationWeeks: number;
  pricePence: number; paidAt: string | null; status: string;
  rejectionReason: string | null; impressions: number; clicks: number; createdAt: string;
};

const STATUS_TABS = ["all", "pending_review", "active", "paused", "rejected"];

export default function AdminFeedAds() {
  useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const { toast } = useToast();
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState("all");
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(false);
  const [ratePounds, setRatePounds] = useState("");
  const [durationPounds, setDurationPounds] = useState<Record<number, string>>({});
  const [savingRate, setSavingRate] = useState(false);
  const [targets, setTargets] = useState<{ stores: { id: number; name: string }[]; deals: { id: number; name: string }[]; events: { id: number; name: string }[] }>({ stores: [], deals: [], events: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const emptyForm = { businessName: "", headline: "", subtext: "", imageUrl: "", ctaText: "Learn more", ctaType: "url", ctaValue: "", durationWeeks: 4, noExpiry: true };
  const [f, setF] = useState(emptyForm);
  const [creating, setCreating] = useState(false);

  function openNew() { setEditingId(null); setF(emptyForm); setShowAdd(true); }
  function openEdit(ad: Ad) {
    setEditingId(ad.id);
    setF({ businessName: ad.businessName ?? "", headline: ad.headline, subtext: ad.subtext ?? "", imageUrl: ad.imageUrl ?? "", ctaText: ad.ctaText, ctaType: ad.ctaType, ctaValue: ad.ctaValue, durationWeeks: ad.durationWeeks, noExpiry: !ad.endsAt });
    setShowAdd(true);
  }

  async function createAd() {
    if (!f.headline.trim() || !f.ctaValue.trim()) { toast({ title: "Headline and link required", variant: "destructive" }); return; }
    setCreating(true);
    try {
      const r = editingId
        ? await fetch(`/api/admin/feed-ads/${editingId}/edit`, { method: "PATCH", headers: authHeaders, body: JSON.stringify(f) })
        : await fetch("/api/admin/feed-ads", { method: "POST", headers: authHeaders, body: JSON.stringify(f) });
      if (!r.ok) throw new Error((await r.json()).error || "Failed");
      toast({ title: editingId ? "Advert updated" : "Advert created & live" });
      setShowAdd(false); setEditingId(null); setF(emptyForm);
      void load();
    } catch (e) { toast({ title: e instanceof Error ? e.message : "Failed", variant: "destructive" }); }
    finally { setCreating(false); }
  }

  function applyPricing(d: { ratePerWeek: number; durations: { weeks: number; pricePence: number }[] }) {
    if (d?.ratePerWeek != null) setRatePounds((d.ratePerWeek / 100).toFixed(2));
    if (Array.isArray(d?.durations)) {
      const m: Record<number, string> = {};
      d.durations.forEach(x => { m[x.weeks] = (x.pricePence / 100).toFixed(2); });
      setDurationPounds(m);
    }
  }

  useEffect(() => {
    fetch("/api/ads/pricing").then(r => r.json()).then(applyPricing).catch(() => {});
    fetch("/api/admin/ad-targets", { headers: authHeaders }).then(r => r.ok ? r.json() : null).then(d => { if (d) setTargets({ stores: d.stores ?? [], deals: d.deals ?? [], events: d.events ?? [] }); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function savePricing() {
    setSavingRate(true);
    try {
      const durations: Record<string, number> = {};
      Object.entries(durationPounds).forEach(([w, v]) => { durations[w] = Math.round((Number(v) || 0) * 100); });
      const r = await fetch("/api/admin/feed-ad-pricing", { method: "PATCH", headers: authHeaders, body: JSON.stringify({ ratePerWeek: Math.round((Number(ratePounds) || 0) * 100), durations }) });
      if (r.ok) { applyPricing(await r.json()); toast({ title: "Pricing updated" }); } else toast({ title: "Failed to save pricing", variant: "destructive" });
    } finally { setSavingRate(false); }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = tab === "all" ? "" : `?status=${tab}`;
      const r = await fetch(`/api/admin/feed-ads${q}`, { headers: authHeaders });
      const d = await r.json();
      setAds(d.ads ?? []);
    } catch { toast({ title: "Could not load ads", variant: "destructive" }); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { void load(); }, [load]);

  async function act(id: number, action: string, body?: object) {
    const r = await fetch(`/api/admin/feed-ads/${id}/${action}`, { method: "PATCH", headers: authHeaders, body: body ? JSON.stringify(body) : undefined });
    if (r.ok) { toast({ title: `Ad ${action}d` }); void load(); }
    else { const d = await r.json().catch(() => ({})); toast({ title: d.error || "Failed", variant: "destructive" }); }
  }

  return (
    <AdminLayout>
      <div className="p-4 max-w-4xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Megaphone size={22} /> Feed Adverts</h1>
          <button onClick={openNew} className="flex items-center gap-1 bg-primary text-white px-3 py-2 rounded-lg text-sm font-medium"><Plus size={16} /> New advert</button>
        </div>
        <p className="text-sm text-gray-500 mb-3">Self-serve adverts bought by businesses. Approve to put them live in the app feeds.</p>

        <div className="mb-4 bg-gray-50 border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-semibold text-sm">Advert pricing</div>
              <div className="text-xs text-gray-500">Set the exact price for each duration. Leave a duration blank/£0 to auto-calculate from the base weekly rate.</div>
            </div>
            <button onClick={savePricing} disabled={savingRate} className="text-sm bg-emerald-600 text-white px-4 py-2 rounded-lg disabled:opacity-50">{savingRate ? "Saving…" : "Save pricing"}</button>
          </div>
          <div className="flex items-center gap-2 mb-3 pb-3 border-b">
            <span className="text-sm">Base rate £</span>
            <input value={ratePounds} onChange={e => setRatePounds(e.target.value)} type="number" step="0.01" className="border rounded px-2 py-1 w-24" />
            <span className="text-sm text-gray-500">per week (used to auto-calculate any duration left blank)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {[1, 2, 4, 8, 12].map(w => (
              <div key={w} className="text-center">
                <div className="text-xs font-medium text-gray-600 mb-1">{w} week{w > 1 ? "s" : ""}</div>
                <div className="flex items-center border rounded px-2 py-1 bg-white">
                  <span className="text-sm text-gray-400">£</span>
                  <input value={durationPounds[w] ?? ""} onChange={e => setDurationPounds(p => ({ ...p, [w]: e.target.value }))} type="number" step="0.01" placeholder="auto" className="w-full outline-none text-sm text-center" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {STATUS_TABS.map(s => (
            <button key={s} onClick={() => setTab(s)} className={`px-3 py-1.5 rounded-full text-sm border ${tab === s ? "bg-emerald-600 text-white border-emerald-600" : ""}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        {loading ? <p className="text-gray-500">Loading…</p> : (
          <div className="space-y-3">
            {ads.length === 0 && <p className="text-gray-500">No adverts here.</p>}
            {ads.map(ad => (
              <div key={ad.id} className="border rounded-xl p-3 flex gap-3">
                {ad.imageUrl ? <img src={ad.imageUrl} alt="" className="w-24 h-24 object-cover rounded-lg shrink-0" /> : <div className="w-24 h-24 bg-gray-100 rounded-lg shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{ad.headline}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${ad.status === "active" ? "bg-emerald-100 text-emerald-700" : ad.status === "pending_review" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{ad.status.replace("_", " ")}</span>
                  </div>
                  {ad.businessName && <div className="text-sm text-gray-500">{ad.businessName}</div>}
                  {ad.subtext && <div className="text-sm text-gray-600">{ad.subtext}</div>}
                  <div className="text-xs text-gray-500 mt-1">
                    CTA: {ad.ctaText} → {ad.ctaType}:{ad.ctaValue} · Feeds: {ad.placements.join(", ")}
                    {ad.towns.length > 0 && <> · Towns: {ad.towns.join(", ")}</>}
                    · {ad.durationWeeks}w · £{(ad.pricePence / 100).toFixed(2)} {ad.paidAt ? "PAID" : "unpaid"}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><BarChart2 size={12} /> {ad.impressions} views · {ad.clicks} clicks</div>
                  {ad.rejectionReason && <div className="text-xs text-red-500 mt-0.5">Rejected: {ad.rejectionReason}</div>}
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => openEdit(ad)} className="flex items-center gap-1 text-xs border px-2 py-1 rounded text-primary"><Pencil size={12} /> Edit</button>
                    {(ad.status === "pending_review") && ad.paidAt && (
                      <button onClick={() => act(ad.id, "approve")} className="flex items-center gap-1 text-xs bg-emerald-600 text-white px-2 py-1 rounded"><Check size={12} /> Approve</button>
                    )}
                    {ad.status !== "rejected" && (
                      <button onClick={() => { const reason = prompt("Rejection reason?") || ""; if (reason || confirm("Reject without reason?")) act(ad.id, "reject", { reason }); }} className="flex items-center gap-1 text-xs border px-2 py-1 rounded text-red-600"><X size={12} /> Reject</button>
                    )}
                    {ad.status === "active" && <button onClick={() => act(ad.id, "pause", { paused: true })} className="flex items-center gap-1 text-xs border px-2 py-1 rounded"><Pause size={12} /> Pause</button>}
                    {ad.status === "paused" && <button onClick={() => act(ad.id, "pause", { paused: false })} className="flex items-center gap-1 text-xs border px-2 py-1 rounded"><Play size={12} /> Resume</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAdd && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setShowAdd(false)}>
            <div className="bg-white rounded-xl p-5 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold">{editingId ? "Edit advert" : "New advert (house / comp)"}</h2>
                <button onClick={() => setShowAdd(false)}><X size={20} /></button>
              </div>
              <p className="text-xs text-gray-500 mb-3">{editingId ? "Update this advert's content. Changes go live immediately across the feeds." : "Created live immediately, no payment. Shows in every feed. Use for Ayrshire Hub promos or comped businesses."}</p>
              <div className="space-y-3">
                <div><label className="text-sm font-medium">Image (optional)</label><ImageUpload value={f.imageUrl} onChange={url => setF({ ...f, imageUrl: url })} /></div>
                <label className="block text-sm">Business name<input value={f.businessName} onChange={e => setF({ ...f, businessName: e.target.value })} className="w-full border rounded px-2 py-1 mt-1" /></label>
                <label className="block text-sm">Headline *<input value={f.headline} onChange={e => setF({ ...f, headline: e.target.value })} className="w-full border rounded px-2 py-1 mt-1" /></label>
                <label className="block text-sm">Sub-text<input value={f.subtext} onChange={e => setF({ ...f, subtext: e.target.value })} className="w-full border rounded px-2 py-1 mt-1" /></label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block text-sm">Button text<input value={f.ctaText} onChange={e => setF({ ...f, ctaText: e.target.value })} className="w-full border rounded px-2 py-1 mt-1" /></label>
                  <label className="block text-sm">Links to<select value={f.ctaType} onChange={e => setF({ ...f, ctaType: e.target.value, ctaValue: "" })} className="w-full border rounded px-2 py-1 mt-1"><option value="url">Website</option><option value="phone">Phone</option><option value="sms">Text (SMS)</option><option value="store">A store</option><option value="deal">A deal</option><option value="event">An event</option></select></label>
                </div>
                {(f.ctaType === "store" || f.ctaType === "deal" || f.ctaType === "event") ? (() => {
                  const list = f.ctaType === "store" ? targets.stores : f.ctaType === "deal" ? targets.deals : targets.events;
                  return list.length === 0 ? (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">No {f.ctaType}s exist yet.</div>
                  ) : (
                    <label className="block text-sm">Choose {f.ctaType} *
                      <select value={f.ctaValue} onChange={e => setF({ ...f, ctaValue: e.target.value })} className="w-full border rounded px-2 py-1 mt-1">
                        <option value="">Select a {f.ctaType}…</option>
                        {list.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                      </select>
                    </label>
                  );
                })() : (
                  <label className="block text-sm">{f.ctaType === "url" ? "Website URL" : "Phone number"} *<input value={f.ctaValue} onChange={e => setF({ ...f, ctaValue: e.target.value })} placeholder={f.ctaType === "url" ? "https://…" : "01292 000000"} className="w-full border rounded px-2 py-1 mt-1" /></label>
                )}
                {!editingId && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.noExpiry} onChange={e => setF({ ...f, noExpiry: e.target.checked })} /> No expiry (runs until paused)</label>}
                {!editingId && !f.noExpiry && <label className="block text-sm">Weeks<input type="number" value={f.durationWeeks} onChange={e => setF({ ...f, durationWeeks: Number(e.target.value) || 1 })} className="w-24 border rounded px-2 py-1 mt-1" /></label>}
                <button onClick={createAd} disabled={creating} className="w-full bg-primary text-white py-2 rounded-lg font-medium disabled:opacity-50">{creating ? "Saving…" : editingId ? "Save changes" : "Create & publish"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
